// src/services/authService.js - Updated with auth events

import api from './api';
import { jwtDecode } from 'jwt-decode';

const authService = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('refreshToken', response.data.refresh_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Dispatch login event
      window.dispatchEvent(new CustomEvent('auth:login', { detail: response.data.user }));
    }
    return response.data;
  },
  
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('refreshToken', response.data.refresh_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Dispatch login event
      window.dispatchEvent(new CustomEvent('auth:login', { detail: response.data.user }));
    }
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    
    // Dispatch logout event
    window.dispatchEvent(new CustomEvent('auth:logout'));
  },
  
  getCurrentUser: () => {
    try {
      // First check if token is still valid
      const token = localStorage.getItem('token');
      if (!token) return null;
      
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        // Token expired - clean up
        if (decoded.exp <= currentTime) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          return null;
        }
      } catch (e) {
        return null;
      }
      
      const userData = JSON.parse(localStorage.getItem('user'));
      if (userData) return userData;
      
      // Fallback to token if user data is not in localStorage
      const decoded = jwtDecode(token);
      return decoded.user || null;
    } catch (error) {
      console.error('Failed to get current user', error);
      return null;
    }
  },
  
  setCurrentUser: (user) => {
    localStorage.setItem('user', JSON.stringify(user));
    // Dispatch update event
    window.dispatchEvent(new CustomEvent('auth:update', { detail: user }));
  },
  
  // Refresh user data from server
  refreshUserData: async () => {
    try {
      const response = await api.get('/auth/profile');
      const freshUserData = response.data.user;
      
      // Update localStorage with fresh data
      localStorage.setItem('user', JSON.stringify(freshUserData));
      
      // Dispatch update event
      window.dispatchEvent(new CustomEvent('auth:update', { detail: freshUserData }));
      
      return freshUserData;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      throw error;
    }
  },
  
  isAuthenticated: () => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    try {
      // Check token expiration
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      if (decoded.exp <= currentTime) {
        // Token expired - clean up
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        return false;
      }
      
      return true;
    } catch (error) {
      return false;
    }
  },
  
  hasRole: async (roleName) => {
    try {
      const response = await api.get(`/auth/verify-role/${roleName}`);
      return response.data[`is${roleName.charAt(0).toUpperCase() + roleName.slice(1)}`];
    } catch (error) {
      console.error(`Failed to verify ${roleName} role`, error);
      return false;
    }
  },
  
  updateProfile: async (userData) => {
    const response = await api.put('/auth/profile', userData);
    
    // Update localStorage with new user data
    if (response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
      window.dispatchEvent(new CustomEvent('auth:update', { detail: response.data.user }));
    }
    
    return response.data;
  },
  
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    
    // Update localStorage with fresh user data
    if (response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    
    return response.data;
  },

  getToken: () => {
    return localStorage.getItem('token');
  },

  getAuthHeaders: () => {
    const token = localStorage.getItem('token');
    return token ? {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    } : {
      'Content-Type': 'application/json'
    };
  },
};

export default authService;